import React, { useState, useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { TabNavigation } from './components/TabNavigation'
import { StyleShotGrid } from './components/StyleShotGrid'
import { StyleClipGrid } from './components/StyleClipGrid'
import { Pagination } from './components/Pagination'
import { useStyleShotData } from './hooks/useStyleShotData'
import { useStyleClipData } from './hooks/useStyleClipData'
import {
  GalleryTabType,
  StyleShotItem,
  StyleClipItem,
  isStyleShotItem,
} from './types'
import useDeleteSynthesisJob from 'hooks/query/aiStudio/useDeleteSynthesisJob'
import useDeleteVideoSynthesisRequest from 'hooks/query/aiStudio/useDeleteVideoSynthesisRequest'

const ITEMS_PER_PAGE = 20

export const AIStudioGallery: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // URL 파라미터에서 초기 값 설정
  const getTabFromURL = (): GalleryTabType => {
    const tab = searchParams.get('tab')
    return tab === 'style-clip' || tab === 'style-shot' ? tab : 'style-shot'
  }

  const getPageFromURL = (): number => {
    const page = searchParams.get('page')
    const parsedPage = page ? parseInt(page, 10) : 1
    return parsedPage > 0 ? parsedPage : 1
  }

  const getLimitFromURL = (): number => {
    const limit = searchParams.get('limit')
    const parsedLimit = limit ? parseInt(limit, 10) : ITEMS_PER_PAGE
    return parsedLimit > 0 ? parsedLimit : ITEMS_PER_PAGE
  }

  const [activeTab, setActiveTab] = useState<GalleryTabType>(getTabFromURL())
  const [currentPage, setCurrentPage] = useState(getPageFromURL())
  const [itemsPerPage, setItemsPerPage] = useState(getLimitFromURL())

  // 분리된 훅들 사용
  const styleShotData = useStyleShotData({
    page: currentPage,
    itemsPerPage: itemsPerPage,
    enabled: activeTab === 'style-shot',
  })

  const styleClipData = useStyleClipData({
    page: currentPage,
    itemsPerPage: itemsPerPage,
    enabled: activeTab === 'style-clip',
  })

  // 현재 탭에 따라 적절한 데이터 선택
  const currentData = activeTab === 'style-shot' ? styleShotData : styleClipData

  // 삭제 훅들
  const deleteSynthesisJobMutation = useDeleteSynthesisJob()
  const deleteVideoSynthesisRequestMutation = useDeleteVideoSynthesisRequest()

  // URL 파라미터 업데이트 함수
  const updateURLParams = (
    tab?: GalleryTabType,
    page?: number,
    limit?: number,
  ) => {
    const newParams = new URLSearchParams(searchParams)

    if (tab !== undefined) newParams.set('tab', tab)
    if (page !== undefined) newParams.set('page', page.toString())
    if (limit !== undefined) newParams.set('limit', limit.toString())

    setSearchParams(newParams)
  }

  const handleTabChange = (tab: GalleryTabType) => {
    setActiveTab(tab)
    setCurrentPage(1)
    updateURLParams(tab, 1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    updateURLParams(undefined, page)

    // 페이지 변경 시 상단으로 부드럽게 스크롤
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const handleSwitchToStyleClip = () => {
    setActiveTab('style-clip')
    setCurrentPage(1)
    updateURLParams('style-clip', 1)
  }

  // URL 파라미터 변경 감지 및 상태 동기화
  useEffect(() => {
    const urlTab = getTabFromURL()
    const urlPage = getPageFromURL()
    const urlLimit = getLimitFromURL()

    if (urlTab !== activeTab) setActiveTab(urlTab)
    if (urlPage !== currentPage) setCurrentPage(urlPage)
    if (urlLimit !== itemsPerPage) setItemsPerPage(urlLimit)
  }, [searchParams])

  // 타입별 삭제 핸들러
  const handleDeleteStyleShot = async (item: StyleShotItem) => {
    try {
      await deleteSynthesisJobMutation.mutateAsync(item.jobId)
      styleShotData.refetch()
    } catch (error) {
      console.error('Style Shot 삭제 실패:', error)
    }
  }

  const handleDeleteStyleClip = async (item: StyleClipItem) => {
    try {
      await deleteVideoSynthesisRequestMutation.mutateAsync(parseInt(item.id))
      styleClipData.refetch()
    } catch (error) {
      console.error('Style Clip 삭제 실패:', error)
    }
  }

  if (currentData.error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="mb-4 text-red-500">
            갤러리를 불러오는 중 오류가 발생했습니다.
          </p>
          <p className="text-gray-600">{currentData.error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen justify-center bg-white">
      <div
        className={`w-full ${
          isMobile ? 'max-w-[375px] px-4' : 'max-w-[1280px] px-6'
        } py-8`}
      >
        {/* Header */}
        <div className="relative mb-6 text-center">
          <h1 className="text-[32px] font-bold text-[#2E343D]">
            스타일룸 생성내역
          </h1>
        </div>

        {/* Content Container */}
        <div className="flex flex-col items-center">
          {/* Tab Navigation */}
          <div className="w-full md:max-w-[816px]">
            <TabNavigation
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </div>

          {/* Gallery Grid - 탭별 컴포넌트 렌더링 */}
          <div className="w-full md:max-w-[816px]">
            {activeTab === 'style-shot' ? (
              <StyleShotGrid
                items={styleShotData.data.items}
                onDownload={styleShotData.downloadItem}
                onSwitchToStyleClip={handleSwitchToStyleClip}
                onRefetchVideos={styleClipData.refetch}
                onStartVideoPolling={styleClipData.startVideoPolling}
                onDelete={handleDeleteStyleShot}
                isLoading={styleShotData.isLoading}
                currentPage={currentPage}
                totalPages={styleShotData.data.totalPages}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPrevPage={() => handlePageChange(currentPage - 1)}
              />
            ) : (
              <StyleClipGrid
                items={styleClipData.data.items}
                onDownload={styleClipData.downloadItem}
                onDelete={handleDeleteStyleClip}
                isLoading={styleClipData.isLoading}
                currentPage={currentPage}
                totalPages={styleClipData.data.totalPages}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPrevPage={() => handlePageChange(currentPage - 1)}
              />
            )}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={currentData.data.totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  )
}
